/**
 * Data-loading and persistence hooks.
 *
 * Every screen in both apps has the same three states — loading, failed, or
 * loaded — and the same need to refetch when an underlying value changes. These
 * hooks encode that once so screens do not each invent a slightly different
 * interpretation of "still loading".
 *
 * They are carried over from the previous component kit unchanged: they hold no
 * styling, so the move to utility classes does not touch them.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useWindowDimensions } from 'react-native';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** True only for the very first load, so a refresh does not blank the screen. */
  initial: boolean;
  reload: () => void;
  /** Replaces the cached value without a round trip, for optimistic updates. */
  setData: (value: T | null) => void;
}

/**
 * Runs an async loader and tracks its state.
 *
 * The loader is held in a ref and keyed by `deps`, so passing an inline arrow
 * function does not cause an infinite refetch loop — the single most common bug
 * in hand-rolled data loading.
 *
 * Results from a superseded call are discarded, which matters when a merchant
 * taps between queue filters faster than the network responds.
 *
 * @param loader Produces the value; may throw.
 * @param deps Values that should trigger a reload.
 * @returns The loading state, the value, and a reload function.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [initial, setInitial] = useState(true);
  const [nonce, setNonce] = useState(0);

  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;
    let cancelled = false;

    setLoading(true);
    setError(null);

    loaderRef
      .current()
      .then((value) => {
        if (cancelled || current !== generation.current) return;
        setData(value);
      })
      .catch((cause: unknown) => {
        if (cancelled || current !== generation.current) return;
        setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        if (cancelled || current !== generation.current) return;
        setLoading(false);
        setInitial(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, initial, reload, setData };
}

/**
 * Runs an async action with pending and error state, for form submissions.
 *
 * Kept separate from {@link useAsync} because an action is triggered by a tap
 * rather than by mounting, and its error belongs next to the button that caused
 * it.
 *
 * @returns A `run` function plus its pending and error state.
 */
export function useAction(): {
  run: <T>(action: () => Promise<T>) => Promise<T | null>;
  pending: boolean;
  error: string | null;
  clearError: () => void;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async <T,>(action: () => Promise<T>): Promise<T | null> => {
    setPending(true);
    setError(null);
    try {
      const result = await action();
      return result;
    } catch (cause) {
      if (mounted.current) {
        setError(cause instanceof Error ? cause.message : String(cause));
      }
      return null;
    } finally {
      if (mounted.current) setPending(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { run, pending, error, clearError };
}

/**
 * Debounces a value.
 *
 * Used by the cart: a customer tapping "+" four times should produce one quote
 * request, not four.
 *
 * @param value The value to debounce.
 * @param delayMs Quiet period before the value settles.
 * @returns The debounced value.
 */
export function useDebounced<T>(value: T, delayMs = 350): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Persists a value in `localStorage` where available.
 *
 * Falls back to in-memory state on platforms without web storage, so the hook is
 * safe to call from a native build.
 *
 * @param key Storage key.
 * @param initialValue Value used when nothing is stored.
 * @returns The value and a setter.
 */
export function usePersistentState<T>(key: string, initialValue: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof localStorage === 'undefined') return initialValue;
    try {
      const stored = localStorage.getItem(key);
      return stored ? (JSON.parse(stored) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      if (typeof localStorage === 'undefined') return;
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Quota or private-browsing failures must not break the screen.
      }
    },
    [key]
  );

  return [value, set];
}

/** Viewport class, from the width the layout actually has to work with. */
export type Breakpoint = 'phone' | 'tablet' | 'desktop';

export interface Responsive {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  /** True from the tablet breakpoint up. */
  isTablet: boolean;
  isDesktop: boolean;
  /** Column count for grids of cards. */
  columns: number;
}

/**
 * Current viewport classification.
 *
 * @returns Viewport width, breakpoint, and a suggested grid column count.
 */
export function useResponsive(): Responsive {
  const { width, height } = useWindowDimensions();

  const breakpoint: Breakpoint = width >= 1180 ? 'desktop' : width >= 768 ? 'tablet' : 'phone';

  // Grid widths are chosen against a comfortable 260pt minimum card, which is
  // where a dish photo and a name still fit without truncating.
  const columns = width >= 1400 ? 4 : width >= 1080 ? 3 : width >= 720 ? 2 : 1;

  return {
    width,
    height,
    breakpoint,
    isTablet: width >= 768,
    isDesktop: width >= 1180,
    columns,
  };
}

/**
 * Calls a function on an interval while a condition holds.
 *
 * Used for realtime fallback polling: when the websocket drops, the merchant
 * queue still refreshes, just less promptly.
 *
 * @param callback Function to run.
 * @param intervalMs Period; pass null to pause.
 */
export function useInterval(callback: () => void, intervalMs: number | null): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (intervalMs === null) return;
    const id = setInterval(() => callbackRef.current(), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}
